import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useMe } from '../../lib/session';
import { fmtCountdown, fmtTime, useNow } from '../../lib/format';
import type { MyLearning } from '../../lib/library';
import { Card, CardHeader, Pill } from '../ui';
import { TypeBadge } from './parts';

/** "To learn" on the Today view (INT-DSH-01, INT-LIB-05, INT-ENR-03): required items I haven't finished. Hidden when there are none. */
export function RequiredLearning() {
  const me = useMe();
  const tz = me.data?.timezone ?? 'UTC';
  const now = useNow(60_000);
  // under the 'today' key so live updates that refresh Today refresh this too
  const { data } = useQuery({ queryKey: ['today', 'learning'], queryFn: () => api<MyLearning[]>('/library/learning/mine'), refetchInterval: 5 * 60_000 });
  const open = (data ?? []).filter((l) => !l.completedAt);
  if (!open.length) return null;
  return (
    <section aria-label="To learn">
      <Card tone={open.some((l) => l.overdue) ? 'down' : 'accent'}>
        <CardHeader
          title={
            <>
              To learn <span className="font-normal text-muted">· {open.length} required</span>
            </>
          }
          right={
            <Link to="/library" className="text-sm font-semibold">
              Library →
            </Link>
          }
        />
        <ul className="m-0 flex list-none flex-col p-0">
          {open.map((l) => (
            <li key={l.assignmentId} className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-[#1a1f27] px-4 py-3 first:border-t-0">
              <TypeBadge type={l.item.type} />
              <div className="flex min-w-0 flex-[1_1_200px] flex-col">
                <Link to={l.item.link} className="font-semibold text-ink hover:text-accent">
                  {l.item.title}
                </Link>
                <span className="text-xs text-muted">
                  Due {fmtTime(l.dueAt, tz, true)} · from {l.assignedBy}
                  {l.viewedAt ? ' · opened' : ''}
                </span>
              </div>
              <Pill tone={l.overdue ? 'down' : new Date(l.dueAt).getTime() - now < 86400_000 ? 'warn' : 'accent'}>{l.overdue ? `Overdue ${fmtCountdown(l.dueAt, now).replace('overdue ', '')}` : `Due ${fmtCountdown(l.dueAt, now)}`}</Pill>
              <Link to={l.item.link} aria-label={`Open ${l.item.title}`} className="rounded-lg border border-[#2b3340] bg-[#1a1f27] px-3.5 py-2 text-sm font-semibold text-ink no-underline">
                Open
              </Link>
            </li>
          ))}
        </ul>
      </Card>
    </section>
  );
}
